import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/react-hooks';

import { mq, space, getProperty, useReveal } from '@ccg/core';
import {
  Stage,
  Container,
  Link,
  TeaserContainer,
  Typography,
  SpecialHeadline,
  Icon,
  RevealBox
} from '@ccg/ligalux';

import relatedCasesQuery from '../../../graphql/queries/relatedCases.graphql';

import Embeds from '../../shared/Embeds/Embeds';
import PageTemplate from '../templates/page';

import { withTranslation } from '../../../i18n';

const RelatedCaseTeaser = styled.div`
  ${getProperty('grid-column', { default: '1 / span 6', m: '1 / span 12' })};

  > div {
    overflow: visible;
  }

  .relatedCaseTeaser__subline {
    margin-bottom: ${space(4)};
  }

  .relatedCaseTeaser__icon {
    transition: padding 200ms ease-in-out;

    ${mq('m', 'max')} {
      display: none;
    }
  }

  h2 {
    display: inline;
  }

  a:hover {
    .relatedCaseTeaser__icon {
      padding-left: ${space(2)};
    }
  }
`;

const Case = ({ data, site, t }) => {
  if (!data.entry) return null;
  const [ref, isVisible] = useReveal();

  const { cookieLayer, entry, footer, teaserSettings, seoDefaults, navigation, social } = data;
  const { casesOverviewPage } = navigation;
  const { relatedCasesCount } = teaserSettings;

  const {
    contentType,
    image,
    video,
    loop,
    solutions,
    industries,
    tasks,
    client,
    title,
    subline,
    excerpt,
    embedsCopy,
    embeds
  } = entry;

  const limit = relatedCasesCount || 1;

  const { data: relatedData } = useQuery(relatedCasesQuery, {
    variables: {
      site,
      currentId: entry.id,
      limit,
      relatedTo: [...solutions, ...industries, ...tasks].map(item => parseFloat(item.id))
    }
  });

  const relatedEntries = relatedData && relatedData.relatedEntries;

  return (
    <PageTemplate
      navigation={navigation}
      seo={entry}
      footer={footer}
      social={social}
      seoDefaults={seoDefaults}
      cookieLayer={cookieLayer}
    >
      <Stage
        variant={contentType === 'advertising' ? 'default' : 'standard'}
        image={
          image &&
          image.length > 0 && {
            ...image[0],
            srcset: {
              '1440w': image[0].width1440,
              '960w': image[0].width960,
              '720w': image[0].width720,
              '375w': image[0].width375
            }
          }
        }
        video={video && video.length > 0 && video[0]}
        loop={loop}
        title={title}
        headline={title}
        subline={subline}
        newsFormat={client[0].title}
        excerpt={excerpt}
        breadcrumbLink={
          casesOverviewPage && casesOverviewPage[0]
            ? {
                label: t('navigation.back'),
                href: {
                  pathname: '/standard',
                  query: { uri: casesOverviewPage[0].uri }
                },
                asPath: `/${casesOverviewPage[0].uri}`
              }
            : undefined
        }
      />

      <Embeds embedsCopy={embedsCopy} embeds={embeds} />

      {relatedEntries && relatedEntries.length > 0 && (
        <Container>
          <TeaserContainer>
            {relatedEntries.map(item => (
              <RelatedCaseTeaser key={item.id} ref={ref}>
                <RevealBox isVisible={isVisible}>
                  <Link
                    asPath={`/${item.uri}`}
                    href={{ pathname: '/case', query: { slug: item.slug } }}
                    title={item.title}
                    invertedHover
                    variant="hero"
                  >
                    <Typography variant="subline" className="relatedCaseTeaser__subline">
                      {item.client && item.client.length > 0 && item.client[0].title}
                    </Typography>
                    <SpecialHeadline as="h2" variant="inherit" text={item.title} />
                    <Icon icon="rightArrow" alignment="right" className="relatedCaseTeaser__icon" />
                  </Link>
                </RevealBox>
              </RelatedCaseTeaser>
            ))}
          </TeaserContainer>
        </Container>
      )}
    </PageTemplate>
  );
};

Case.propTypes = {
  t: PropTypes.func.isRequired,
  site: PropTypes.string.isRequired,
  data: PropTypes.shape().isRequired
};

Case.defaultProps = {};

export default withTranslation('common')(Case);
